import { useCallback, useEffect, useRef } from 'react';

import { AnyDateType, pluralize, present, toDate, toSeconds } from '@art-suite/art-core-ts';
import { ApiScan, getScanDataMetadataQueryKey, getScanDataQueryKey } from '@kaiizen/kai-pro-api';
import { mergeScanDataInMemory, ScanDataInMemory } from '@kaiizen/kai-scan-template-system';
import { QUERY_CACHE_NO_CHANGE, QueryCache, useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';

import { useScanDataSyncModel } from '../contexts/syncing/Models';
import { useKaiProApiClient } from '../useKaiProApiClient';
import { deltaFetchesEnabled } from './deltaFetchGate';
import { ScanEditSyncGate } from './ScanEditSyncGate';
import { ScanOfflineDataMinTtlOnAccessMs } from './ScanStateLib';

const DEBUG_LOG = false;

// A refetch that lands while a local edit hasn't reached the server can
// rebuild the on-screen scan root from data that doesn't include that edit,
// visually reverting it - see KAIDEV-4175 and ScanEditSyncGate.ts. Cap how
// many times a single fetch invocation retries itself when edits keep
// landing continuously, rather than looping forever.
const MAX_REFETCH_EPOCH_RETRIES = 5;

/**
 * CachedScanDataDeltaMetadata is the metadata stored in the cache
 * It contains the from and to dates.
 *
 * @from The from date is the date to get the data from.
 * @to The to date is the date of the most recent data in the cache.
 */
type CachedScanDataDeltaMetadata = {
  syncedAfter?: AnyDateType;
  from: AnyDateType;
  to: AnyDateType;
};

/**
 * CachedScanDataWithDeltaMetadata is the actual data stored in the cache
 * It contains the data, baselineData and deltaData.
 *
 * @data The data is always all data from time-zero through now.
 * @baselineData The baselineData is only set if 'from' is provided. This is all data from time-zero through <= 'from'.
 * @deltaData The deltaData is only set if 'from' is provided. This is the delta from baselineData to data.
 */
type CachedScanDataWithDeltaMetadata = CachedScanDataDeltaMetadata & {
  data: ScanDataInMemory[];
  baselineData?: ScanDataInMemory[];
  deltaData?: ScanDataInMemory[];
  loadCount: number;
};

/**
 * Hook that returns the scan root data for a given scan id.
 * Note: this does not return scanDataMutators - use useScanContext if you need to mutate scan data.
 * @param scanId - The id of the scan to get the root data for.
 * @returns The scan root data.
 */
export const useScanData = (scan?: ApiScan, options?: { prefetchOnly?: boolean; editSyncGate?: ScanEditSyncGate }) => {
  const scanId = scan?.id;
  const kaiProApiClient = useKaiProApiClient();
  const prefetchOnly = options?.prefetchOnly ?? false;
  const editSyncGate = options?.editSyncGate;
  const scanDataSyncModel = useScanDataSyncModel();
  const from = scan?.parentScanAt ? toDate(scan.parentScanAt) : undefined;
  const scanDataQueryKey = getScanDataQueryKey(scanId, from);

  // True while there's a local edit for this scan that hasn't reached the
  // server yet - either still sitting in the save debounce, or handed to the
  // sync queue but not yet confirmed synced (including failed attempts,
  // which must not be silently treated as "resolved"). See KAIDEV-4175.
  const hasUnsyncedLocalEdits = useCallback(async (): Promise<boolean> => {
    if (!scanId) return false;
    if (editSyncGate?.hasPendingLocalEdit()) return true;
    const pendingRecords = await scanDataSyncModel.getAll(record => record.scanId === scanId);
    return pendingRecords.length > 0;
  }, [scanId, editSyncGate, scanDataSyncModel]);

  const scanDataQuery = useCachedQuery<CachedScanDataWithDeltaMetadata>(
    scanDataQueryKey,
    async (key, queryCache, { setProgress }) => {
      const scanDataQueryCache = queryCache as unknown as QueryCache<CachedScanDataWithDeltaMetadata>;
      if (!scanId) return QUERY_CACHE_NO_CHANGE;

      // Defer this fetch if a local edit hasn't reached the server yet -
      // committing fetched data now could rebuild the on-screen scan root
      // from data that doesn't include that edit, visually reverting it. A
      // deferred fetch is retried once syncing settles (see the effect
      // below) - see KAIDEV-4175.
      if (editSyncGate && await hasUnsyncedLocalEdits()) {
        editSyncGate.setRefetchPending(true);
        return QUERY_CACHE_NO_CHANGE;
      }

      const queryCacheForCustomScanDataMetadata = queryCache as unknown as QueryCache<CachedScanDataDeltaMetadata>;
      const metadataKey = getScanDataMetadataQueryKey(scanId, from);

      const doFetch = async (): Promise<CachedScanDataWithDeltaMetadata | typeof QUERY_CACHE_NO_CHANGE> => {
        const startTime = toSeconds();
        const logPrefix = `useScanData fetch ${scanId}` + (from ? ` from ${from.toISOString()}` : '');
        // fetch only the metadata to keep memory footprint low for ReactNative
        const cachedScanDataDeltaMetadata = (await queryCacheForCustomScanDataMetadata.cache.getEntry(metadataKey, undefined, true))?.value;

        let result: CachedScanDataWithDeltaMetadata | undefined = undefined;

        // if we can do a delta fetch, do it
        if (
          deltaFetchesEnabled() && cachedScanDataDeltaMetadata && present(cachedScanDataDeltaMetadata.to) && present(cachedScanDataDeltaMetadata.from)
          && await queryCache.cache.has(key)
        ) {
          // delta fetch
          if (DEBUG_LOG) {
            console.log(
              `${logPrefix} delta: fetching delta from server: syncedAfter: ${
                cachedScanDataDeltaMetadata.syncedAfter ? toDate(cachedScanDataDeltaMetadata.syncedAfter).toISOString() : 'undefined'
              } to: ${toDate(cachedScanDataDeltaMetadata.to).toISOString()}`,
            );
          }
          const fetched = await kaiProApiClient.getScanData(scanId, {
            syncedAfter: toDate(cachedScanDataDeltaMetadata.syncedAfter ?? cachedScanDataDeltaMetadata.to),
          });
          if (DEBUG_LOG && fetched.data.length > 0) {
            console.log(
              `${logPrefix} delta: fetched ${pluralize(fetched.data.length, 'ScanData record')} from server (took ${(toSeconds() - startTime) * 1000 | 0}ms)`,
              scanId,
            );
          }

          // if the delta fetch returns no data, there is nothing new, we can use the cached full entry as-is
          if (fetched.data.length === 0) {
            return QUERY_CACHE_NO_CHANGE;
          }

          // fetch the actual cached data (we wait as long as possible to do this to keep memory footprint low for ReactNative)
          const cachedScanData = (await scanDataQueryCache.cache.getEntry(key))?.value;
          const previousLoadCount = cachedScanData?.loadCount ?? 0;

          // do we have everything we need to merge the delta?
          if (cachedScanData && cachedScanData.data.length > 0) {
            cachedScanData.from = toDate(cachedScanData.from);
            cachedScanData.to = toDate(cachedScanData.to);
            const mergedScanData = mergeScanDataInMemory(cachedScanData.data, fetched.data);
            if (DEBUG_LOG) {
              console.log(
                `${logPrefix} delta: ${pluralize(fetched.data.length, 'ScanData record')} from server (total: ${mergedScanData.length}, took ${
                  (toSeconds() - startTime) * 1000 | 0
                }ms)`,
                scanId,
              );
            }
            result = {
              ...cachedScanData,
              data: mergedScanData,
              deltaData: cachedScanData.deltaData ? mergeScanDataInMemory(cachedScanData.deltaData, fetched.data) : fetched.data,
              to: fetched.to,
              syncedAfter: fetched.syncedAfter,
              loadCount: previousLoadCount + 1,
            };
          } // else, we'll do a full fetch
        }

        if (!result) {
          // full fetch
          if (DEBUG_LOG) {
            console.log(
              `${logPrefix} full fetch: fetching all scan data... (deltaFetchesEnabled: ${deltaFetchesEnabled()}, have cached data: ${!!await queryCache
                .cache
                .has(key)})`,
              cachedScanDataDeltaMetadata,
            );
          }
          const localResult = await kaiProApiClient.getScanData(scanId, { from, includeBaseline: !!from }).then(result => ({
            ...result,
            loadCount: 0,
          }));
          result = localResult; // this helps typescript know that result is not undefined (for some reason)
          if (result.baselineData) {
            result = {
              ...result,
              data: mergeScanDataInMemory(result.baselineData, result.data), // at first, result.data only contains a delta `from` to now; so we need to merge it with baseline data to get the, up-to-date scan
              deltaData: result.data,
            };
          }
          if (DEBUG_LOG) {
            console.log(
              `${logPrefix} full fetch: fetched ${pluralize(result.data.length, 'ScanData')} from server (took ${(toSeconds() - startTime) * 1000 | 0}ms)`,
              scanId,
            );
            console.log(`${logPrefix} full fetch: result: from: ${toDate(result.from)}, to: ${toDate(result.to)}, data: ${result.data?.length}`);
          }
        }
        return result;
      };

      for (let attempt = 0; attempt < MAX_REFETCH_EPOCH_RETRIES; attempt++) {
        const epochAtStart = editSyncGate?.getEditEpoch() ?? 0;
        const result = await doFetch();

        // An edit landed while this fetch's own network round-trip was in
        // flight - the result we just fetched may already be stale. Re-check
        // whether we're still synced (rather than assuming another retry is
        // needed) and either retry or defer, exactly as at the top.
        if (editSyncGate && editSyncGate.getEditEpoch() !== epochAtStart) {
          if (await hasUnsyncedLocalEdits()) {
            editSyncGate.setRefetchPending(true);
            return QUERY_CACHE_NO_CHANGE;
          }
          continue;
        }

        if (result !== QUERY_CACHE_NO_CHANGE) {
          if (DEBUG_LOG) console.log(`useScanData fetch ${scanId} saving metadata to cache: from: ${toDate(result.from)}, to: ${toDate(result.to)}`);
          await queryCacheForCustomScanDataMetadata.cache.setPrefetched(metadataKey, { from: toDate(result.from), to: toDate(result.to) });
        }
        return result;
      }

      // Edits kept landing continuously - defer and let the next natural
      // refetch trigger (or the sync-settled retry below) try again, rather
      // than looping forever.
      editSyncGate?.setRefetchPending(true);
      return QUERY_CACHE_NO_CHANGE;
    },
    {
      enabled: !!kaiProApiClient.token && !!scanId,
      prefetchOnly,
      minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs,
    },
  );

  // A refetch deferred above (editSyncGate.setRefetchPending(true)) needs an
  // explicit retry once syncing settles - nothing else re-triggers it. The
  // sync model's own subscription fires on every write to the sync queue
  // (including the queue draining as records get confirmed synced), so this
  // re-checks on each of those and retries once we're actually caught up.
  const refetchRef = useRef(scanDataQuery.refetch);
  refetchRef.current = scanDataQuery.refetch;
  useEffect(() => {
    if (!editSyncGate || !scanId) return;
    const retryIfSettled = async () => {
      if (!editSyncGate.isRefetchPending()) return;
      if (await hasUnsyncedLocalEdits()) return;
      editSyncGate.setRefetchPending(false);
      refetchRef.current();
    };
    return scanDataSyncModel.subscribe(() => {
      retryIfSettled();
    });
  }, [editSyncGate, scanId, scanDataSyncModel, hasUnsyncedLocalEdits]);

  const { getCurrent: getCurrentCachedScanData, update: setCachedScanData } = scanDataQuery;
  const updateCachedScanData = useCallback(async (newScanData: ScanDataInMemory[]): Promise<ScanDataInMemory[]> => {
    const cachedScanDataWithDelta = await getCurrentCachedScanData();

    const updatedScanData = cachedScanDataWithDelta?.data ? mergeScanDataInMemory(cachedScanDataWithDelta.data, newScanData) : newScanData;
    if (cachedScanDataWithDelta) {
      await setCachedScanData({
        ...cachedScanDataWithDelta,
        data: updatedScanData,
        deltaData: mergeScanDataInMemory(cachedScanDataWithDelta?.deltaData || [], newScanData),
      });
    }
    return updatedScanData;
  }, [getCurrentCachedScanData, setCachedScanData]);

  return {
    ...scanDataQuery,
    scanData: scanDataQuery.data?.data,
    baselineData: scanDataQuery.data?.baselineData,
    deltaData: scanDataQuery.data?.deltaData,
    loadCount: scanDataQuery.data?.loadCount || -1,
    updateCachedScanData,
  };
};

/**
 * Hook that returns a function to fetch the latest scan data triggering updates for any components using the useScanData hook.
 * Note that this only fetches the latest scan data delta, it does not re-fetch any already-fetched scan data.
 * @returns A function that fetches the latest scan data delta for the given scan ID
 */
export const useScanDataDeltaFetch = () => {
  const queryCache = useQueryCache<any>();
  return useCallback(async (scanId: string): Promise<void> => {
    const prefix = getScanDataQueryKey(scanId);
    await queryCache.refetchAll({ prefix });
  }, [queryCache]);
};
